import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import {
  Card, CardContent, Typography, Box, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button,
  IconButton, Switch, Chip, makeStyles, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Tooltip, TextField, InputAdornment, Snackbar
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import RepeatIcon from '@material-ui/icons/Repeat';
import CampaignIcon from '@material-ui/icons/Announcement';
import LaunchIcon from '@material-ui/icons/Launch';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { getCampaignById, getCampaigns, deleteCampaign, toggleCampaignStatus, resendCampaign } from '../../services/campaignService';
import CampaignFormModal from './CampaignFormModal';

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
  },
  createBtn: {
    borderRadius: 12,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    fontWeight: 600,
    padding: '8px 20px',
    boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  imageThumb: {
    width: 60,
    height: 40,
    borderRadius: 8,
    objectFit: 'cover',
    background: 'rgba(148,163,184,0.1)',
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableRow: {
    '&:hover': {
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
    },
  },
  searchField: {
    maxWidth: 280,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    },
  },
  dateField: {
    maxWidth: 180,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    },
  },
}));

function getFrequencyLabel(frequency, maxImpressions) {
  if (frequency === 'once_ever') return 'Once per device';
  if (frequency === 'once_per_day') {
    return maxImpressions && maxImpressions > 0
      ? `1x/day (Max ${maxImpressions}x)`
      : '1x/day';
  }
  if (frequency === 'every_open') return 'Every open';
  return 'Configured';
}

export default function CampaignListPage() {
  const classes = useStyles();
  const history = useHistory();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const handleOpenCreateModal = () => {
    setSelectedCampaignId(null);
    setFormModalOpen(true);
  };

  const handleOpenEditModal = (campId) => {
    setSelectedCampaignId(campId);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedCampaignId(null);
  };

  const handleFormSuccess = () => {
    setSnackbar({
      open: true,
      message: selectedCampaignId ? 'ক্যাম্পেইন সফলভাবে আপডেট করা হয়েছে!' : 'নতুন ক্যাম্পেইন সফলভাবে তৈরি করা হয়েছে!'
    });
    loadData();
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    const data = await getCampaigns();
    setCampaigns(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggle = async (id, currentStatus) => {
    await toggleCampaignStatus(id, currentStatus);
    loadData();
  };

  const handleResend = async (id) => {
    try {
      await resendCampaign(id);
      setSnackbar({ open: true, message: 'Campaign resent! Devices will receive and display it again.' });
      loadData();
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to resend campaign' });
    }
  };

  const handleDelete = async () => {
    if (deleteDialog.id) {
      await deleteCampaign(deleteDialog.id);
      setDeleteDialog({ open: false, id: null });
      setSnackbar({ open: true, message: 'Campaign deleted' });
      loadData();
    }
  };

  // Filter campaigns by search text and date
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      if (search && search.trim()) {
        const q = search.trim().toLowerCase();
        const tEn = (c.title_en || c.title || '').toLowerCase();
        const tBn = (c.title_bn || '').toLowerCase();
        const bEn = (c.body_en || c.body || '').toLowerCase();
        const bBn = (c.body_bn || '').toLowerCase();
        if (!tEn.includes(q) && !tBn.includes(q) && !bEn.includes(q) && !bBn.includes(q)) {
          return false;
        }
      }

      if (dateFilter && dateFilter.trim()) {
        const cDate = c.createdAt ? (c.createdAt.toDate ? c.createdAt.toDate() : new Date(c.createdAt)) : null;
        if (cDate) {
          const dateStr = cDate.toISOString().split('T')[0];
          if (dateStr !== dateFilter) return false;
        }
      }

      return true;
    });
  }, [campaigns, search, dateFilter]);

  return (
    <AdminLayout title="In-App Messaging Campaigns" onRefresh={loadData}>
      <Card className={classes.card}>
        <CardContent style={{ padding: 24 }}>
          <div className={classes.header}>
            <div>
              <Typography className={classes.title}>
                Active & Scheduled In-App Campaigns
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                Control how many days campaigns appear and how often they show per device.
              </Typography>
            </div>
            <Button
              variant="contained"
              className={classes.createBtn}
              startIcon={<AddIcon />}
              onClick={handleOpenCreateModal}
            >
              Create Campaign
            </Button>
          </div>

          {/* Search & Filter Toolbar */}
          <Box display="flex" alignItems="center" style={{ gap: 12 }} mb={3} flexWrap="wrap">
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search by title or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" style={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <TextField
              size="small"
              type="date"
              variant="outlined"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={classes.dateField}
              InputProps={{
                endAdornment: dateFilter ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setDateFilter('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {(search || dateFilter) && (
              <Button
                size="small"
                onClick={() => { setSearch(''); setDateFilter(''); }}
                style={{ color: '#94a3b8', textTransform: 'none' }}
              >
                Reset filters
              </Button>
            )}
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={36} />
            </Box>
          ) : filteredCampaigns.length === 0 ? (
            <Box p={6} textAlign="center" color="text.secondary">
              <CampaignIcon style={{ fontSize: 48, color: 'rgba(148,163,184,0.4)', marginBottom: 12 }} />
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {search || dateFilter ? 'No matching campaigns found' : 'No campaigns created yet'}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                {search || dateFilter
                  ? 'Try adjusting your search criteria or date filter.'
                  : 'Click "Create Campaign" to push an announcement to all app users.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeaderCell}>Banner</TableCell>
                    <TableCell className={classes.tableHeaderCell}>Title (EN / BN)</TableCell>
                    <TableCell className={classes.tableHeaderCell}>Schedule & Frequency</TableCell>
                    <TableCell className={classes.tableHeaderCell}>Action Link</TableCell>
                    <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                    <TableCell align="right" className={classes.tableHeaderCell}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCampaigns.map((c) => (
                    <TableRow key={c.id} className={classes.tableRow}>
                      <TableCell>
                        {c.imageUrl ? (
                          <img src={c.imageUrl} alt="Banner" className={classes.imageThumb} />
                        ) : (
                          <div className={classes.imageThumb} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CampaignIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                          {c.title_en || c.title || 'Untitled'}
                        </Typography>
                        {c.title_bn && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {c.title_bn}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <Chip
                            size="small"
                            icon={<ScheduleIcon style={{ fontSize: 13 }} />}
                            label={
                              c.durationDays && c.durationDays > 0
                                ? `${c.durationDays} Days Duration`
                                : 'Permanent'
                            }
                            style={{ fontSize: '0.75rem', width: 'fit-content' }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {getFrequencyLabel(c.displayFrequency, c.maxImpressions)}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.actionUrl ? (
                          <Tooltip title={`Open Link: ${c.actionUrl}`} arrow>
                            <Chip
                              size="small"
                              clickable
                              icon={<LaunchIcon style={{ fontSize: 12 }} />}
                              label={c.buttonText_en || c.actionUrl}
                              variant="outlined"
                              style={{ fontSize: '0.75rem', cursor: 'pointer' }}
                              onClick={() => {
                                let url = c.actionUrl.trim();
                                if (!url.startsWith('http://') && !url.startsWith('https://')) {
                                  url = 'https://' + url;
                                }
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">None</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={c.isActive ? 'Active - showing to app users' : 'Inactive'} arrow>
                          <Switch
                            checked={Boolean(c.isActive)}
                            onChange={() => handleToggle(c.id, c.isActive)}
                            color="primary"
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Resend / Push to Users Again" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleResend(c.id)}
                            style={{ color: '#10b981', marginRight: 4 }}
                          >
                            <RepeatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Campaign" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditModal(c.id)}
                            style={{ marginRight: 4 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Campaign" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteDialog({ open: true, id: c.id })}
                            style={{ color: '#ef4444' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Delete Campaign?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this in-app messaging campaign? Users will no longer see this message.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} style={{ color: '#ef4444', fontWeight: 700 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <CampaignFormModal
        open={formModalOpen}
        campaignId={selectedCampaignId}
        onClose={handleCloseFormModal}
        onSuccess={handleFormSuccess}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </AdminLayout>
  );
}
